import { Popover, Tooltip } from "@mui/material";
import "./Sorting.scss";
import { Fragment, useEffect, useState } from "react";
import { DndContext, DragOverlay, useDroppable } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const Sorting = ({
  properties,
  showHeaderOrder,
  onOrderChange,
  ignoreProperties,
  displayButton = true,
  displayColumn = false,
  hideArrows = false,
  initialSort,
}) => {
  const [order, setOrder] = useState([
    initialSort ? initialSort : Object.keys(properties)[0],
  ]);
  const [asc, setAsc] = useState({
    [initialSort ? initialSort : Object.keys(properties)[0]]: true,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [droppedColumns, setdroppedColumns] = useState(order);
  const [availableColumns, setAvailablecolumns] = useState(
    Object.keys(properties).slice(1)
  );
  const [preApplyAsc, setPreApplyAsc] = useState(asc);

  const [activeId, setActiveId] = useState(null);
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;
    const activeList = active.data.current?.source;
    const overList = over.data.current?.source;
    if (activeList && overList && activeList !== overList) {
      if (activeList === "available") {
        setAvailablecolumns((items) => items.filter((item) => item !== activeId));
        setdroppedColumns((items) => [...items, activeId]);
        setPreApplyAsc((prev) => ({ ...prev, [activeId]: true }));
      } else {
        setAvailablecolumns((items) => [...items, activeId]);
        setdroppedColumns((items) => items.filter((item) => item !== activeId));
        setPreApplyAsc((prev) => {
          const copy = { ...prev };
          delete copy[activeId];
          return copy;
        });
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) {
      setActiveId(null);
      return;
    }
    const activeId = active.id;
    const overId = over.id;
    const activeList = active.data.current?.source;
    const overList = over.data.current?.source;

    if (overId === "available" && activeList !== overId) {
      setAvailablecolumns((prev) => [...prev, activeId]);
      setdroppedColumns((prev) => prev.filter((i) => i !== activeId));
    } else if (overId === "selected" && activeList !== overId) {
      setdroppedColumns((prev) => [...prev, activeId]);
      setAvailablecolumns((prev) => prev.filter((i) => i !== activeId));
      setPreApplyAsc((prev) => ({ ...prev, [activeId]: true }));
    } else if (activeList === overList) {
      const items = activeList === "available" ? availableColumns : droppedColumns;
      const oldIndex = items.indexOf(activeId);
      const newIndex = items.indexOf(overId);
      if (oldIndex !== newIndex) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        if (activeList === "available") {
          setAvailablecolumns(newItems);
        } else {
          setdroppedColumns(newItems);
        }
      }
    }
  };

  const handleRemoveColumn = (key) => {
    setAvailablecolumns((prev) => [...prev, key]);
    setdroppedColumns((prev) => prev.filter((i) => i !== key));
    setPreApplyAsc((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const isAsc = (key) => asc[key] === true;
  const isDesc = (key) => asc[key] === false;
  const isPreApplyAsc = (key) => preApplyAsc[key] === true;
  const isPreApplyDesc = (key) => preApplyAsc[key] === false;

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const updateAsc = (key, ascending) => {
    setOrder((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setAsc((prev) => ({ ...prev, [key]: ascending }));
  };

  const updatePreAsc = (key, ascending) => {
    setPreApplyAsc((prev) => ({ ...prev, [key]: ascending }));
  };

  const applySelection = () => {
    setOrder(droppedColumns);
    setAsc(preApplyAsc);
  };

  useEffect(() => {
    setdroppedColumns(order);
    setAvailablecolumns(
      Object.keys(properties).filter((key) => !order.includes(key))
    );
    setPreApplyAsc(asc);
    onOrderChange?.(
      order.map((p) => `${p} ${asc[p] ? "asc" : "desc"}`).join(",")
    );
  }, [order, asc]);

  const loopKey = (key) => {
    if (!asc.hasOwnProperty(key)) updateAsc(key, true);
    else if (asc[key]) updateAsc(key, false);
    else {
      setOrder((prev) => prev.filter((p) => p !== key));
      setAsc((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  return (
    <div className="sorting-component">
      <div className="columns">
        {Object.entries(properties).map(([key, val]) => {
          const label = typeof val === "string" ? val : val.label;
          const align = typeof val === "object" && val.align ? val.align : "left";
          return (
            <div
              key={key}
              className={`column ${displayColumn ? "column-direction" : ""} align-${align}`}
            >
              <b onClick={() => loopKey(key)}>{label}</b>
              {!hideArrows && (
                <Tooltip
                  arrow
                  title={isAsc(key) ? "Ascending" : isDesc(key) ? "Descending" : ""}
                >
                  <i
                    onClick={() => loopKey(key)}
                    className={`arrow-button ${
                      isAsc(key)
                        ? "icon-arrowup"
                        : isDesc(key)
                        ? "icon-arrowdown"
                        : "icon-hyphen"
                    } ${isAsc(key) || isDesc(key) ? "active" : ""}`}
                  >
                    {showHeaderOrder && order.includes(key) && (
                      <u>{order.indexOf(key) + 1}</u>
                    )}
                  </i>
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>
      {displayButton && (
        <button className="filled" onClick={handleClick}>
          Sorting Columns
        </button>
      )}
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className="sorting-menu">{/* ...rest unchanged... */}</div>
      </Popover>
    </div>
  );
};
